/**
 * @summary - Component that Provides Vendor Details List Page
 * @author Purushothaman T
 * @since 2024-07-29
 */
import { Box, Stack } from "@mui/material";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import { BemsGraph, FemsGraph, MasterGraph } from "../../utility/apiURLS";
import { vendordetails } from "../../utility/constants/masters";
import { dialogContent } from "../../utility/message";
import { getAuthToken, getcompanyId } from "../../utility/tokenUtility";
import CloseButton from "../commoncomponents/CloseButton";
import CreateNewBtn from "../commoncomponents/CreateNewBtn";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomDialog from "../commoncomponents/CustomDialog";
import PageTitle from "../commoncomponents/PageTitle";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import SectionTitle from "../commoncomponents/SectionTitle";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

function VendorDetailsList() {
  const column = vendordetails.gridColumn;
  const [rowData, setRowData] = useState([]);
  const [gridApi, setGridApi] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [contentDialog, setContentDialog] = useState("");
  const [vendorData, setVendorData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "POMM002");

  const navigate = useNavigate();
  useEffect(() => {
    fetchData();
  }, [filterModel, sortModel, pageIndex, pageSize]);
  const fetchData = async () => {
    setRowData([]);
    setIsLoading(true);
    try {
      // Obtain the bearer token from local storage or your state management system
      const token = getAuthToken("id_token");
      const companyId = getcompanyId();
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };
      const vendorDetailsResponse = await axios.get(
        `${MasterGraph}/api/GraphMaspomvendors/GetMaspomvendorsListByCompanyId/${companyId}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setRowCount(vendorDetailsResponse.data.totalCount);
      setExportCount(vendorDetailsResponse.data.totalCount)
      setRowData(vendorDetailsResponse.data.items);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
    finally{
      setIsLoading(false);
    }
  };

  const colDefs = [
    { field: column.vendorcode.field, headerName: column.vendorcode.header },
    { field: column.vendorname.field, headerName: column.vendorname.header },
    { field: column.status.field, headerName: column.status.header },
    { field: column.companytype.field, headerName: column.companytype.header },
    { field: column.category.field, headerName: column.category.header },
    { field: column.group.field, headerName: column.group.header },
    { field: column.service.field, headerName: column.service.header },
  ];


  const fetchExportdata = async () => {
   
    try {
      // Obtain the bearer token from local storage or your state management system
      const token = getAuthToken("id_token");
      const companyId = getcompanyId();
      const config = {
        headers: {
          // Attach bearer token to request header
          Authorization: `Bearer ${token}`,
        },
      };
      const vendorDetailsResponse = await axios.get(
        `${MasterGraph}/api/GraphMaspomvendors/GetExportData/${companyId}?filterOptions=${JSON.stringify(
        filterModel
      )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setExportData(vendorDetailsResponse.data)

    } catch (error) {
      console.error("Error fetching options:", error);
    }
    finally{
      setIsLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigate("/vendordetails/form");
  };
  const handleEdit = (data) => {
    navigate("/vendordetails/form", {
      state: {
        id: data.id,
        isEditMode: true,
      },
    });
  };

  const handleDelete = (data) => {
    setOpenDeleteDialog(true);
    setVendorData(data);
  };

  const handleView = (data) => {
    navigate("/vendordetails/form", {
      state: {
        id: data.id,
        isViewMode: true,
      },
    });
  };

  const handleConfirmDelete = async () => {
    try {
      const token = getAuthToken("id_token");
      const companyId = getcompanyId();
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const [responseFems, responseBems] = await Promise.all([
        axios.get(
          `${FemsGraph}/api/Vendor/CheckVendor/${companyId}/${vendorData.id}`,
          config
        ),
        axios.get(
          `${BemsGraph}/api/Vendor/CheckVendor/${companyId}/${vendorData.id}`,
          config
        ),
      ]);
      if (responseFems.data.code == 0 && responseBems.data.code == 0) {
        const responseMaster = await axios.delete(
          `${MasterGraph}/api/maspomvendors/${companyId}/${vendorData.id}`,
          config
        );
        if (responseMaster.status === 204) {
          setOpenDialog(true);
          setContentDialog(dialogContent.deleted);
          fetchData();
        }
      } else {
        setOpenDialog(true);
        setOpenDeleteDialog(false);
        setContentDialog("Vendor was in use. Cannot be deleted.");
      }
    } catch (error) {
      if (error.response.status === 400) {
        setOpenDialog(true);
        setContentDialog(error.response.data.detail);
      }
      return error.response;
    }
  };

  const handleDialogClose = () => {
    setOpenDeleteDialog(false);
    setOpenDialog(false);
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };


  return (
    <>
      <PageTitle title={"Vendor Details"} />
      <DefaultCard>
        <Stack
          mb={1}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <SectionTitle title={"Vendor Details List"} />

          <Stack direction={"row"} spacing={1.5}>
            {access?.canCreate && <CreateNewBtn onClick={handleCreateClick} />}
            <RefreshExportButtons
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
                handleRefresh={handleRefresh}
              title={"Vendor Details"}
            />
          </Stack>
        </Stack>
        <Box>
          <CustomAgGrid
            rows={rowData}
            columns={colDefs}
            refSet={gridApi}
            setGridApi={setGridApi}
            isDateComparator={true}
            handleEdit={access?.canEdit && handleEdit}
            handleDelete={access?.canDelete && handleDelete}
            handleView={access?.canView && handleView}
            pagination={false}
            isLoading={isLoading}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            totalCount={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
          />
        </Box>
      </DefaultCard>
      <CloseButton />
      <CustomDialog
        open={openDeleteDialog}
        onConfirm={handleConfirmDelete}
        content={dialogContent.confirmDelete}
        cancelBtn={"No"}
        confirmBtn={"Yes"}
        onClose={handleDialogClose}
      />
      <CustomDialog
        open={openDialog}
        content={contentDialog}
        confirmBtn={"Ok"}
        onConfirm={handleDialogClose}
      />
    </>
  );
}

export default VendorDetailsList;
